import clsx from "clsx";
import { upperCase } from "lodash";
import { BiImage } from "react-icons/bi";
import { useEffect, useState } from "react";
import { Disclosure } from "@headlessui/react";
import { NAVIGATION_ROUTES } from "@/app/routes";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { useArrayConstants } from "@/app/hooks/useArrayConstants";

const RBACDisclosure: React.FC<{
  onChange: (modules: string[], actions: string[]) => void;
  allAccess: boolean;
  defaultSelectedModules: string[];
  defaultSelectedActions: string[];
}> = ({ allAccess, defaultSelectedActions, defaultSelectedModules, onChange }) => {
  const { RBACExcludedNavs } = useArrayConstants();
  const [RBACDescription, setRBACDescription] = useState<string>(null);
  const [screen, setScreen] = useState<string>(null);
  const [modules, setModules] = useState<string[]>([]);
  const [actions, setActions] = useState<string[]>([]);

  const getModuleActions = (moduleName: string) => {
    const moduleActions = [];
    Object.keys(NAVIGATION_ROUTES)?.forEach((moduleKey) => {
      if (moduleKey === moduleName && !RBACExcludedNavs.includes(moduleName)) {
        const actions = Object.keys(NAVIGATION_ROUTES[moduleKey].children);
        Object.keys(NAVIGATION_ROUTES[moduleKey].children).map((pageKey) => {
          const subPageKeys = Object.keys(NAVIGATION_ROUTES[moduleKey].children[pageKey].children);
          subPageKeys.forEach((key) => !RBACExcludedNavs.includes(subPageKeys) && actions.push(key));
        });
        moduleActions.push([...actions]);
      }
    });
    return moduleActions.flat();
  };

  const handleAddModule = (moduleName: string) => {
    const newModules = [];
    const newActions = [];
    const moduleActions = getModuleActions(moduleName);
    if (modules.includes(moduleName)) {
      const nm = modules.filter((p) => p !== moduleName);
      const na = actions.filter((p) => !moduleActions.includes(p));
      setModules(nm);
      setActions(na);
      newModules.push(...nm);
      newActions.push(...na);
    } else {
      const nm = [...modules, moduleName];
      const na = [...actions, ...moduleActions];
      setModules(nm);
      setActions(na);
      newModules.push(...nm);
      newActions.push(...na);
    }
    onChange(newModules, newActions);
  };

  const handleAddActions = (actionName: string) => {
    const newActions = [];
    if (actions.includes(actionName)) {
      const na = actions.filter((a) => a !== actionName);
      setActions(na);
      newActions.push(...na);
    } else {
      const na = [...actions, actionName];
      setActions(na);
      newActions.push(...na);
    }
    onChange(modules, newActions);
  };

  useEffect(() => {
    if (allAccess === true) {
      const allModules = Object.keys(NAVIGATION_ROUTES).filter((m) => !RBACExcludedNavs.includes(m));
      const allActions = [];
      Object.keys(NAVIGATION_ROUTES).forEach((moduleKey) => {
        allActions.push(...getModuleActions(moduleKey));
      });
      setModules(allModules);
      setActions(allActions);
      onChange(allModules, allActions);
    } else {
      setModules([]);
      setActions([]);
      onChange([], []);
    }
  }, [allAccess]);

  useEffect(() => {
    if (defaultSelectedActions?.length > 0) {
      setActions(defaultSelectedActions);
    }
  }, [defaultSelectedActions]);

  useEffect(() => {
    if (defaultSelectedModules?.length > 0) {
      setModules(defaultSelectedModules);
    }
  }, [defaultSelectedModules]);

  return (
    <div className="sm:grid sm:grid-cols-2 sm:gap-4 mt-5">
      <div
        className={`w-full h-full mx-2 border border-slate-400 border-opacity-40 rounded-lg divide-y overflow-hidden ${
          open ? "border-h-20" : "border-h-10"
        }`}
      >
        {Object.keys(NAVIGATION_ROUTES)?.map((moduleKey: string) => {
          return (
            !RBACExcludedNavs.includes(moduleKey) && (
              <Disclosure>
                {({ open }) => {
                  return (
                    <div className={clsx(modules.includes(moduleKey) ? "bg-blue-50" : "bg-gray-50")}>
                      <div className={`flex w-full py-2 items-center justify-between px-4 text-left text-sm`}>
                        <div className="flex gap-3 items-center">
                          <input
                            onChange={() => handleAddModule(moduleKey)}
                            type="checkbox"
                            checked={modules.includes(moduleKey)}
                            className="mr-2 cursor-pointer rounded-sm"
                          />
                          <span className="font-semibold text-gray-700">
                            {upperCase(
                              NAVIGATION_ROUTES[moduleKey].RBACDisplayName
                                ? NAVIGATION_ROUTES[moduleKey].RBACDisplayName.split("_")?.join(" ")
                                : upperCase(moduleKey?.split("_")?.join(" ")),
                            )}
                          </span>
                        </div>
                        <Disclosure.Button>
                          <ChevronDownIcon className={`${open ? "rotate-180 transform" : ""} h-5 w-5 text-black`} />
                        </Disclosure.Button>
                      </div>
                      <Disclosure.Panel className="px-4 pb-2 pt-4 grid grid-cols-3 gap-2 bg-gray-50">
                        {Object.keys(NAVIGATION_ROUTES[moduleKey].children).map((pageKey: string) => {
                          return (
                            !RBACExcludedNavs.includes(pageKey) && (
                              <>
                                <div
                                  onMouseEnter={() => {
                                    setRBACDescription(NAVIGATION_ROUTES[moduleKey].children[pageKey].RBACDescription);
                                    setScreen(NAVIGATION_ROUTES[moduleKey].children[pageKey].screen);
                                  }}
                                  key={pageKey}
                                  className="flex items-center text-sm text-gray-800"
                                >
                                  <input
                                    onChange={() => {
                                      handleAddActions(pageKey);
                                    }}
                                    type="checkbox"
                                    checked={actions.includes(pageKey)}
                                    className="mr-2 cursor-pointer rounded-sm"
                                  />{" "}
                                  <span className="text-xs">
                                    {upperCase(
                                      NAVIGATION_ROUTES[moduleKey].children[pageKey].RBACDisplayName
                                        ? NAVIGATION_ROUTES[moduleKey].children[pageKey].RBACDisplayName.split(
                                            "_",
                                          )?.join(" ")
                                        : pageKey.split("_")?.join(" "),
                                    )}
                                  </span>
                                </div>
                                {Object.keys(NAVIGATION_ROUTES[moduleKey].children[pageKey].children)?.length > 0 &&
                                  Object.keys(NAVIGATION_ROUTES[moduleKey].children[pageKey].children)?.map(
                                    (subPageKey: string) => {
                                      return (
                                        !RBACExcludedNavs.includes(subPageKey) && (
                                          <div
                                            onMouseEnter={() => {
                                              setRBACDescription(
                                                NAVIGATION_ROUTES[moduleKey].children[pageKey].children[subPageKey]
                                                  .RBACDescription,
                                              );
                                              setScreen(
                                                NAVIGATION_ROUTES[moduleKey].children[pageKey].children[subPageKey]
                                                  .screen,
                                              );
                                            }}
                                            key={subPageKey}
                                            className="flex items-center text-sm text-gray-800"
                                          >
                                            <input
                                              onChange={() => {
                                                handleAddActions(subPageKey);
                                              }}
                                              type="checkbox"
                                              checked={actions.includes(subPageKey)}
                                              className="mr-2 cursor-pointer rounded-sm"
                                            />{" "}
                                            <span className="text-xs">
                                              {upperCase(
                                                NAVIGATION_ROUTES[moduleKey].children[pageKey].children[subPageKey]
                                                  .RBACDisplayName
                                                  ? NAVIGATION_ROUTES[moduleKey].children[pageKey].children[
                                                      subPageKey
                                                    ].RBACDisplayName.split("_")?.join(" ")
                                                  : subPageKey.split("_").join(" "),
                                              )}
                                            </span>
                                          </div>
                                        )
                                      );
                                    },
                                  )}
                              </>
                            )
                          );
                        })}
                      </Disclosure.Panel>
                    </div>
                  );
                }}
              </Disclosure>
            )
          );
        })}
      </div>
      <div className="border w-full max-h-full rounded-md px-3 py-2">
        <p className="font-semibold text-lg text-gray-600">Action View</p>
        <div className="mt-4">
          {RBACDescription ? (
            <p className="text-xs text-gray-500">{RBACDescription}</p>
          ) : (
            <p className="text-xs text-gray-500">Please select an action to view it's description.</p>
          )}
          {screen ? (
            <div className="mt-2 w-full h-full rounded-md object-fill px-2 py-2">
              <img src={screen} alt="RBAC_SCREEN" className="" />
            </div>
          ) : (
            <div className="mt-3 w-full bg-gray-100 flex justify-center items-center flex-col rounded-md">
              <div className="flex justify-start items-center p-10">
                <BiImage className="w-6 h-6 text-gray-500" />
                <p className="text-gray-600">No descriptive screen uploaded. </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RBACDisclosure;
